/**
 * Go-live maths for a listing.
 *
 * `expiration` is the date a listing goes live on MLS. Everything here compares
 * whole days, matching the mobile app and the server's upcoming filter: a
 * listing counts as live from the moment its go-live date arrives, not from the
 * exact hour it was set to.
 */

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const midnight = (value) => {
  // `new Date(null)` is 1 Jan 1970, not an invalid date — so a listing with no
  // go-live date would otherwise read as decades overdue.
  if (value === null || value === undefined || value === "") return null;

  const d = new Date(typeof value === "string" ? parseInt(value, 10) : value);
  if (Number.isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  return d;
};

/**
 * Whole days from today until the listing goes live. Negative once it is live,
 * `null` when the listing has no go-live date at all.
 */
export const daysUntilGoLive = (expiration, now = new Date()) => {
  const goLive = midnight(expiration);
  if (!goLive) return null;
  return Math.round((goLive - midnight(now)) / MS_PER_DAY);
};

export const isUpcoming = (expiration, now = new Date()) => {
  const days = daysUntilGoLive(expiration, now);
  return days !== null && days > 0;
};

/**
 * The badge a card shows. Upcoming listings count down; anything already live
 * says so, because a stale row is worse than no row when a broker is presenting.
 */
export const goLiveLabel = (expiration, now = new Date()) => {
  const days = daysUntilGoLive(expiration, now);
  if (days === null) return null;
  if (days > 1) return `GOING LIVE IN ${days} DAYS`;
  if (days === 1) return "GOING LIVE TOMORROW";
  if (days === 0) return "GOES LIVE TODAY";
  return "LIVE ON MLS";
};
